import { ChatContextParams } from '@parcel2go/core-ui';
import { useAuth } from 'react-oidc-context';
import { GetAccountId } from '~/apps/smartsend/lib/helpers/AuthHelper';
import { useExtendedDetails } from './use-extended-details';

/**
 * Builds the customer context sent to the live-chat provider when a chat opens.
 *
 * Identity comes from the OIDC profile, which is already in memory - no request is made for it.
 * Only the VIP flag needs the network, and that goes through `useExtendedDetails`, so several chat
 * buttons and the VIP banner on one page share a single call.
 *
 * The keys mirror what the legacy site (`Parcel2Go.Web`) has always sent, so a Talkdesk flow keyed
 * on them matches whichever front end the customer happens to be using.
 *
 * Deliberately not memoised: `visitor_location` has to reflect the page the customer is on when
 * they click, and a memo keyed on profile and details would hold a stale URL across client-side
 * navigation. The object feeds a click handler, so re-creating it per render costs nothing.
 *
 * Pass `enabled: false` where no chat will actually be offered. Hooks cannot be called
 * conditionally, so a caller that decides later whether to render a chat would otherwise fetch VIP
 * data it never uses.
 */
export function useChatContext({ enabled = true }: { enabled?: boolean } = {}): ChatContextParams {
  const auth = useAuth();
  const { details } = useExtendedDetails(enabled);

  const profile = auth?.user?.profile;
  const params: ChatContextParams = {};

  const name = profile?.name;
  // `||` not `??`: an empty-string email claim must fall through to preferred_username, otherwise
  // the truthiness checks below drop a perfectly good identity. Matches require-customer.tsx.
  const email = profile?.email || profile?.preferred_username;
  const phone = profile?.phone_number;

  if (name) {
    params.name = name;
  }

  if (email) {
    params.email = email;
  }

  if (phone) {
    params.phone = phone;
  }

  // GetAccountId interpolates into a string unconditionally, so it returns the literal
  // "undefined" when the claim is absent. Guard on the claim rather than on its result.
  if (profile?.account_number) {
    params.accountNumber = GetAccountId(profile);
  }

  // Origin and path only - never the query string or fragment. Sibling front ends put credentials
  // there (ui-checkout's payment route carries the hash that authorises its order API calls), so
  // forwarding a raw URL to a third party is a habit worth not having. The page is the useful
  // signal for an agent; the parameters never are.
  params.visitor_location = `${window.location.origin}${window.location.pathname}`;

  // Only once the lookup has actually landed. Sending `false` while it is still in flight would
  // label a VIP a non-VIP and route them to the frontline queue - the very bug this fixes - and it
  // would do it intermittently, on whoever happens to click early or load slowly. Absent means
  // unknown; false is a claim we can only make once we have the answer.
  if (details) {
    params.is_vip = details.metadata?.supportGroup === 'VIP';

    const vipStatus = details.metadata?.supportType;

    if (vipStatus) {
      params.vipStatus = vipStatus;
    }
  }

  return params;
}
